import React, {useState, useEffect} from 'react';
import {createPortal} from 'react-dom';
import post from '../utils/post.js';
import ConfirmPopup from './ConfirmPopup.js';

// Částky chodí ze serveru už naformátované na dvě desetinná místa (2 121,00).
const renderPrice = (value) => `${value ?? ''}`;

// Souhrn za položku se počítá až na klientovi, proto ho formátujeme stejně jako
// serverové částky – mezera po tisících, čárka a dvě desetinná místa.
const renderPriceSum = (value) => Number(value || 0).toLocaleString('cs-CZ', {minimumFractionDigits: 2, maximumFractionDigits: 2});

// Počty kusů se stejně jako částky oddělují po tisících, ať jsou dobře čitelné.
const renderCount = (value) => Number(value || 0).toLocaleString('cs-CZ', {maximumFractionDigits: 0});

const MONTH_NAMES = ['Leden', 'Únor', 'Březen', 'Duben', 'Květen', 'Červen', 'Červenec', 'Srpen', 'Září', 'Říjen', 'Listopad', 'Prosinec'];

// monthKey přichází ze serveru ve tvaru YYYY-MM, ať popisek sedí s obdobím dotazu.
const renderMonthLabel = (monthKey) => {
	const [year, month] = String(monthKey || '').split('-');
	const name = MONTH_NAMES[parseInt(month, 10) - 1];

	return name && year ? `${name} ${year}` : '';
};

// Sdílený obsah sekce Eshop sysadmin – používá ho stránka /eshopsysadmin i popup
// s eshopem uživatele v monitoringu. Hledaný výraz je řízený zvenku (stránka ho
// drží v horní liště, popup ho předvyplňuje jménem uživatele). reloadKey vynutí
// nové načtení i beze změny hledání (stránka posílá klíč router location).
export default function EshopSysadminList({usrInfo, searchValue = '', reloadKey, onLoadingChange}) {
	const [loading, setLoading] = useState(false);
	const [data, setData] = useState([]);
	const [dataGroupped, setDataGroupped] = useState([]);
	const [data_groupped_totals, setDataGrouppedTotals] = useState(0);
	const [data_notpayed, setDataNotpayed] = useState([]);
	const [data_notpayed_totals, setDataNotpayedTotals] = useState(0);
	const [showConfirmPopup, setShowConfirmPopup] = useState(false);
	const [itemId, setItemId] = useState(0);
	// offset se čte jen přes funkční update v loadNext, samotná hodnota tady potřeba není.
	const [, setOffset] = useState(0);
	const [loadedCnt, setLoadedCnt] = useState(0);
	const [activeTab, setActiveTab] = useState('admin');
	const [monthOffset, setMonthOffset] = useState(0);
	const [monthKey, setMonthKey] = useState('');
	const [dataMonthly, setDataMonthly] = useState([]);
	const [dataMonthlyPieces, setDataMonthlyPieces] = useState(0);
	const [dataMonthlyTotals, setDataMonthlyTotals] = useState('0,00');
	const [monthlyLoading, setMonthlyLoading] = useState(false);
	const [monthlyLoaded, setMonthlyLoaded] = useState(false);

	useEffect(() => {
		if (onLoadingChange) {
			onLoadingChange(loading);
		}
	}, [loading, onLoadingChange]);

	// Změna hledání (nebo reloadKey) → načteme vždy od začátku.
	useEffect(() => {
		setOffset(0);
		getData(searchValue, 0);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [searchValue, reloadKey]);

	// Měsíční souhrn se tahá zvlášť – načte se až při otevření tabu a pak jen při
	// přepnutí měsíce, aby přepínání neznamenalo znovunačtení celého přehledu.
	useEffect(() => {
		if (activeTab !== 'groupped') {
			return;
		}

		getMonthlyData(monthOffset);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [activeTab, monthOffset]);

	const getMonthlyData = (offsetMonth) => {
		setMonthlyLoading(true);

		post('getSysadminEshopMonthlySold', {monthOffset: offsetMonth})
			.then((res) => {
				setDataMonthly(res.data_monthly || []);
				setDataMonthlyPieces(res.data_monthly_pieces || 0);
				setDataMonthlyTotals(res.data_monthly_totals || '0,00');
				setMonthKey(res.monthKey || '');
				setMonthlyLoading(false);
				setMonthlyLoaded(true);
			})
			.catch((error) => {
				console.error(error);
				setMonthlyLoading(false);
				setMonthlyLoaded(true);
			});
	};

	const loadNext = () => {
		setOffset((prevOffset) => {
			let newOffset = prevOffset + usrInfo.LIST_ROWS;
			getData(searchValue, prevOffset + usrInfo.LIST_ROWS);
			return newOffset;
		});
	};

	const getData = (search, offset) => {
		setLoading(true);

		post('getListSysadminEshop', {search: search, offset: offset})
			.then((res) => {
				if (offset === 0) {
					setData(res.data);
				} else {
					setData((prevRows) => {
						const updatedRows = [...prevRows, ...res.data];
						return updatedRows;
					});
				}

				setLoadedCnt(res.data.length);

				setDataGroupped(res.data_groupped);
				setDataGrouppedTotals(res.data_groupped_totals);
				setDataNotpayed(res.data_notpayed);
				setDataNotpayedTotals(res.data_notpayed_totals);
				setLoading(false);
			})
			.catch((error) => {
				console.error(error);
				setLoading(false);
			});
	};

	const updateEshopItemRowDelete = (row) => {
		const newData = data.filter((item) => {
			return item.ID !== row;
		});

		setData(newData);
	};

	const handleConfirmPopup = (ID) => {
		setItemId(ID);
		setShowConfirmPopup(true);
	};

	const closeConfirmPopup = () => {
		setShowConfirmPopup(false);
	};

	const deleteConfirmPopup = () => {
		setLoading(true);

		post('deleteSysadminEshopItem', {ID: itemId})
			.then((res) => {
				closeConfirmPopup();
				updateEshopItemRowDelete(itemId);
				setLoading(false);
			})
			.catch((error) => {
				console.error(error);
				setLoading(false);
			});
	};

	let list_groupped = [];
	let oldItem = [];
	let cnt = 0;
	let pList = [];
	let groupCnt = 0;
	let groupTotal = 0;

	// Souhrnný řádek dává smysl jen u položek s víc cenovými hladinami – u jediné
	// ceny by pouze zopakoval stejná čísla.
	const withGroupSum = (lines, sumCnt, sumTotal, key) => {
		if (lines.length < 2) {
			return lines;
		}

		return [
			...lines,
			<div className="eshop-page__history-line eshop-page__history-line--sum" key={key}>
				<div className="eshop-page__history-cnt">{renderCount(sumCnt)}</div>
				<div className="eshop-page__history-unit" />
				<div className="eshop-page__history-total">{renderPriceSum(sumTotal)}</div>
			</div>,
		];
	};

	dataGroupped?.forEach((item, index) => {
		++cnt;
		if (cnt === 1) {
			oldItem.ITEMID = item.ITEMID;
		}

		if (oldItem.ITEMID !== item.ITEMID) {
			list_groupped.push(
				<div className="eshop-page__history-group" key={'item_' + index}>
					<div className="eshop-page__history-summary">
						<div className="eshop-page__history-title">{oldItem.NAME}</div>
						<div className="eshop-page__history-lines">{withGroupSum(pList, groupCnt, groupTotal, 'item_sum_' + index)}</div>
					</div>
				</div>,
			);

			pList = [];
			groupCnt = 0;
			groupTotal = 0;
		}

		groupCnt += parseInt(item.CNT, 10) || 0;
		groupTotal += parseFloat(item.PRICE_TOTAL) || 0;

		pList.push(
			<div className="eshop-page__history-line" key={'item_sub_' + index}>
				<div className="eshop-page__history-cnt">{renderCount(item.CNT)}</div>
				<div className="eshop-page__history-unit">x {renderPrice(item.PRICES)}</div>
				<div className="eshop-page__history-total">{renderPrice(item.PRICE_TOTALS)}</div>
			</div>,
		);

		if (cnt === dataGroupped.length) {
			list_groupped.push(
				<div className="eshop-page__history-group" key={'item_' + dataGroupped.length}>
					<div className="eshop-page__history-summary">
						<div className="eshop-page__history-title">{item.NAME}</div>
						<div className="eshop-page__history-lines">{withGroupSum(pList, groupCnt, groupTotal, 'item_sum_last')}</div>
					</div>
				</div>,
			);
		}

		oldItem = item;
	});

	const list_notpayed = data_notpayed?.map((item, index) => {
		const hasPermanentMember = parseInt(item.PERNAMENTMEMBER, 10) === 1;

		return (
			<div className={`eshop-page__ledger-row${hasPermanentMember ? '' : ' eshop-page__ledger-row--danger'} eshop-page__ledger-row--compact`} key={'item_' + index}>
				<div className="eshop-page__ledger-meta">
					<div className="eshop-page__ledger-name">{item.USRNAME}</div>
					<div className="eshop-page__ledger-date">{item.NAME}</div>
				</div>
				<div className="eshop-page__ledger-value">{renderPrice(item.TOPAY_TOTALS)}</div>
			</div>
		);
	});

	const list_monthly = dataMonthly?.map((item, index) => (
		<div className="eshop-page__sold-card" key={'sold_' + (item.ITEMID ?? index)}>
			<div className="eshop-page__sold-thumb">{item.IMG ? <img src={item.IMG} alt="" /> : <span className="eshop-page__sold-thumb-empty" />}</div>
			<div className="eshop-page__sold-name">{item.NAME}</div>
			<div className="eshop-page__sold-count">
				{item.CNT}
				<span className="eshop-page__sold-unit">ks</span>
			</div>
		</div>
	));

	const list = data?.map((item, index) => {
		const today = new Date();
		const logDate = new Date(item.DT_CR);
		const isToday = logDate.getDate() === today.getDate() && logDate.getMonth() === today.getMonth() && logDate.getFullYear() === today.getFullYear();

		return (
			<div className="eshop-page__admin-row" key={'item_' + index}>
				<div className="eshop-page__row-icon">{item.IMG ? <img src={item.IMG} alt="" /> : null}</div>
				<div className="eshop-page__admin-content">
					<div className="eshop-page__admin-user">{item.USRNAME}</div>
					<div className={`eshop-page__admin-date${isToday ? ' eshop-page__admin-date--today' : ''}`}>{item.CRDTS}</div>
					<div className={`eshop-page__admin-item${isToday ? ' eshop-page__admin-item--today' : ''}`}>{item.NAME}</div>
					<div className="eshop-page__admin-price">{renderPrice(item.PRICES)}</div>
				</div>

				{usrInfo.sysAdmin === 1 ? (
					<button type="button" className="eshop-page__icon-button eshop-page__admin-delete" onClick={() => handleConfirmPopup(item.ID)}>
						<img className="icon1 del small" alt="" />
					</button>
				) : (
					<></>
				)}
			</div>
		);
	});

	const summaryCards = [
		{
			id: 'admin',
			label: 'PŘEHLED',
			value: data?.length || 0,
			toneClassName: 'eshop-page__summary-card--info',
		},
		{
			id: 'notpayed',
			label: 'NEZAPLACENO',
			value: data_notpayed?.length || 0,
			toneClassName: 'eshop-page__summary-card--danger',
		},
		{
			id: 'groupped',
			label: 'SOUHRN POLOŽEK',
			value: list_groupped.length || 0,
			toneClassName: 'eshop-page__summary-card--neutral',
		},
	];

	return (
		<>
			{/* Potvrzení smazání jde přes portál do body – v popupu z monitoringu by ho
			    jinak rodičovský popup (backdrop-filter, animace) uzavřel do svého obdélníku. */}
			{createPortal(
				<ConfirmPopup showConfirmPopup={showConfirmPopup} showButtonDelete deleteConfirmPopup={deleteConfirmPopup} closeConfirmPopup={closeConfirmPopup} infoHeader="Smazání záznamu" infoMain="Opravdu chceš smazat tento záznam?" />,
				document.body,
			)}

			<div className="eshop-page__summary-panel">
				<div className="eshop-page__summary-grid">
					{summaryCards.map((card) => {
						const isActive = activeTab === card.id;

						return (
							<button className={`home-summary-card eshop-page__summary-card ${card.toneClassName}${isActive ? ' is-active' : ''}`} key={card.id} type="button" aria-pressed={isActive} onClick={() => setActiveTab(card.id)}>
								<div className="home-summary-card__content">
									<span className="home-summary-card__eyebrow">{card.label}</span>
									<span className="home-summary-card__value">{card.value}</span>
								</div>
							</button>
						);
					})}
				</div>
			</div>

			<div className="eshop-page__content">
				{activeTab === 'notpayed' && (
					<section className="eshop-page__section">
						<div className="eshop-page__section-head">
							<h2 className="eshop-page__section-title">Nezaplaceno</h2>
						</div>
						<div className="eshop-page__ledger-list eshop-page__ledger-list--compact">{list_notpayed.length > 0 ? list_notpayed : <div className="eshop-page__empty">Žádné nezaplacené položky.</div>}</div>
						<div className="eshop-page__total-row eshop-page__total-row--accent" key={'data_notpayed_totals'}>
							<div className="eshop-page__total-label">NEZAPLACENO:</div>
							<div className="eshop-page__total-value">{renderPrice(data_notpayed_totals)}</div>
						</div>
					</section>
				)}

				{activeTab === 'groupped' && (
					<>
						<section className="eshop-page__section eshop-page__section--sold">
							<div className="eshop-page__section-head eshop-page__section-head--split">
								<h2 className="eshop-page__section-title">Prodáno v měsíci</h2>
								<div className="eshop-page__month-nav">
									<button type="button" className="eshop-page__month-button" onClick={() => setMonthOffset((prev) => prev + 1)} aria-label="Předchozí měsíc">
										‹
									</button>
									<span className="eshop-page__month-label">{renderMonthLabel(monthKey) || '–'}</span>
									<button type="button" className="eshop-page__month-button" onClick={() => setMonthOffset((prev) => Math.max(0, prev - 1))} disabled={monthOffset === 0} aria-label="Následující měsíc">
										›
									</button>
								</div>
							</div>

							{list_monthly.length > 0 ? (
								<>
									<div className={`eshop-page__sold-grid${monthlyLoading ? ' is-loading' : ''}`}>{list_monthly}</div>
									<div className="eshop-page__total-row eshop-page__total-row--accent">
										<div className="eshop-page__total-label">PRODÁNO CELKEM:</div>
										<div className="eshop-page__total-value">
											{dataMonthlyPieces} ks ({dataMonthlyTotals})
										</div>
									</div>
								</>
							) : (
								<div className="eshop-page__empty">{monthlyLoaded && !monthlyLoading ? 'V tomto měsíci se nic neprodalo.' : 'Načítám…'}</div>
							)}
						</section>

						<section className="eshop-page__section">
							<div className="eshop-page__section-head">
								<h2 className="eshop-page__section-title">Souhrn prodaných položek</h2>
							</div>
							<div className="eshop-page__history-list">{list_groupped.length > 0 ? list_groupped : <div className="eshop-page__empty">Souhrn zatím neobsahuje žádné položky.</div>}</div>
							<div className="eshop-page__total-row eshop-page__total-row--accent" key={'data_groupped_totals'}>
								<div className="eshop-page__total-label">CELKEM:</div>
								<div className="eshop-page__total-value">{renderPrice(data_groupped_totals)}</div>
							</div>
						</section>
					</>
				)}

				{activeTab === 'admin' && (
					<>
						<section className="eshop-page__section eshop-page__section--admin-records">
							<div className="eshop-page__section-head">
								<h2 className="eshop-page__section-title">Přehled záznamů</h2>
							</div>
							<div className="eshop-page__admin-list">{list.length > 0 ? list : <div className="eshop-page__empty">Přehled záznamů je zatím prázdný.</div>}</div>
						</section>

						<div className="eshop-page__load-more">
							{parseInt(loadedCnt) === parseInt(usrInfo.LIST_ROWS) ? (
								<button type="button" className="button small loadNext eshop-page__button" onClick={() => loadNext()}>
									Načíst další
								</button>
							) : (
								<span className="fnt_12 loadNext eshop-page__load-all">Byly načteny všechny záznamy</span>
							)}
						</div>
					</>
				)}
			</div>
		</>
	);
}
